// Employee App Access (Access Center / App Configuration)
//
// Per-employee toggle for which dApps each person can launch.
// Hard rule: dAdmin, dEpr, dSpr are ADMIN-ONLY apps - their toggles are
// disabled (and forced OFF in the diff) when the employee is not an
// Admin. Other apps (dAssist, dBug, dForm, dTime, dSlip) are togglable
// for every employee tier.
//
// Backend:
//   GET  /api/employee/all                              - employee + app_* flags
//   POST /api/AccessCenter/emp_app_is_active_toggle     - one column at a time
//   GET  /api/employee/logined_employee                 - Admin gate

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import { dnAlert } from "./utils/dNews_Alert";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import "./Emp_App_Access.css";

// App catalog. `topTierOnly` marks apps that can only be granted to
// Admin OR Sub Admin tier employees. dAdmin and dEpr stay top-tier
// because their primary surfaces (dadmin login + stakeholder review
// portal) are admin/manager-driven workflows.
//
// dSpr is NOT topTierOnly - the self-review form must be reachable by
// every employee (User tier included), so the toggle is enabled for all
// roles and defaults to ON. Admin can still flip it off per-employee to
// revoke access (e.g. for terminated staff still on the system).
const APPS = [
    { key: "app_dAdmin",  label: "dAdmin",  topTierOnly: true  },
    { key: "app_dAssist", label: "dAssist", topTierOnly: false },
    { key: "app_dBug",    label: "dBug",    topTierOnly: false },
    { key: "app_dForm",   label: "dForm",   topTierOnly: false },
    { key: "app_dTime",   label: "dTime",   topTierOnly: false },
    { key: "app_dSlip",   label: "dSlip",   topTierOnly: false },
    { key: "app_dEpr",    label: "dEpr",    topTierOnly: true  },
    { key: "app_dSpr",    label: "dSpr",    topTierOnly: false },
    { key: "app_dNews",   label: "dNews",   topTierOnly: true  },
    { key: "app_dAttendance", label: "dAttendance", topTierOnly: false },
];

// Tier check used everywhere `topTierOnly` apps are gated. Returns true
// when the employee is allowed to hold one of those toggles.
const TOP_TIERS = new Set(["Admin", "Sub Admin"]);
function isTopTier(level) { return TOP_TIERS.has(level); }

const ACCESS_LEVEL_META = {
    "Admin":     { bg: "#FEE2E2", fg: "#991B1B" },
    "Sub Admin": { bg: "#FEF3C7", fg: "#92400E" },
    "Manager":   { bg: "#DBEAFE", fg: "#1E3A8A" },
    "User":      { bg: "#F1F5F9", fg: "#475569" },
};
function accessLevelMeta(level) {
    return ACCESS_LEVEL_META[level] || ACCESS_LEVEL_META.User;
}

// Hash-based avatar colour (deterministic per name)
function avatarColor(name) {
    const text = String(name || "").trim() || "User";
    let h = 0;
    for (let i = 0; i < text.length; i++) h = text.charCodeAt(i) + ((h << 5) - h);
    return `hsl(${Math.abs(h % 360)}, 65%, 55%)`;
}
function initials(first, last) {
    return ((first?.[0] || "") + (last?.[0] || "")).toUpperCase() || "?";
}

const EmployeeAppAccess = ({ navSize }) => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [originalEmployees, setOriginalEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [selectedMainFilter, setSelectedMainFilter] = useState("emp_id");
    const [filters, setFilters] = useState({ name: [], emp_id: [], emp_access_level: [] });
    const [search, setSearch] = useState("");

    useEffect(() => {
        fetchloginedEmployees();
        fetchEmployees();
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                navigate("/login"); return;
            }
            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
        } catch (err) {
            console.error("Error fetching logged-in employee:", err);
        }
    };

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/api/employee/all`);
            const data = await res.json();
            const list = Array.isArray(data) ? data.filter(e => !e.deleted_time) : [];
            setEmployees(list);
            setOriginalEmployees(JSON.parse(JSON.stringify(list)));
        } catch (err) {
            console.error("Error fetching employees:", err);
        } finally {
            setLoading(false);
        }
    };

    const mainFilterOptions = [
        { value: "emp_id",           label: "Employee ID" },
        { value: "name",             label: "Employee name" },
        { value: "emp_access_level", label: "Access level" },
    ];

    // Access Level has a deliberate hierarchy order (Admin → Sub Admin →
    // Manager → User). Other filter keys just use DB order.
    const ACCESS_LEVEL_ORDER = ["Admin", "Sub Admin", "Manager", "User"];

    const getFilterValues = (key) => {
        if (key === "name") {
            const u = [...new Set(employees.map(e => `${e.emp_first_name} ${e.emp_last_name}`))];
            return u.map(n => ({ value: n, label: n }));
        }
        const u = [...new Set(employees.map(e => e[key]))].filter(Boolean);
        if (key === "emp_access_level") {
            return u
                .sort((a, b) => {
                    const ai = ACCESS_LEVEL_ORDER.indexOf(a);
                    const bi = ACCESS_LEVEL_ORDER.indexOf(b);
                    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                })
                .map(v => ({ value: v, label: v }));
        }
        return u.map(v => ({ value: v, label: v }));
    };

    // Filter pipeline: dropdown filter + plain search box (search hits emp_id /
    // name / access level). Search is a quick keyboard-only filter so HR can
    // skip the dropdown for a single quick lookup.
    const filteredEmployees = useMemo(() => {
        const q = search.trim().toLowerCase();
        return employees.filter(emp => {
            if (selectedMainFilter === "name") {
                if (filters.name.length > 0) {
                    const fullName = `${emp.emp_first_name} ${emp.emp_last_name}`;
                    if (!filters.name.some(f => f.value === fullName)) return false;
                }
            } else if (filters[selectedMainFilter].length > 0) {
                if (!filters[selectedMainFilter].some(f => f.value === emp[selectedMainFilter])) return false;
            }
            if (q) {
                const hay = `${emp.emp_id} ${emp.emp_first_name} ${emp.emp_last_name} ${emp.emp_access_level || ""} ${emp.emp_mail_id || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [employees, filters, selectedMainFilter, search]);

    // Stats: tally only filtered rows so the counts reflect what HR sees
    const stats = useMemo(() => {
        const total = filteredEmployees.length;
        const admins = filteredEmployees.filter(e => e.emp_access_level === "Admin").length;
        const subAdmins = filteredEmployees.filter(e => e.emp_access_level === "Sub Admin").length;
        const fullAccess = filteredEmployees.filter(emp => {
            const allowed = APPS.filter(a => !a.topTierOnly || isTopTier(emp.emp_access_level));
            return allowed.length > 0 && allowed.every(a => emp[a.key] === 1);
        }).length;
        return { total, admins, subAdmins, fullAccess };
    }, [filteredEmployees]);

    // Per-toggle change. dAdmin/dEpr/dSpr are clamped OFF for Manager/User
    // tiers - the disabled attribute also blocks clicks in the UI, but
    // this is the belt-and-suspenders defence in case a row sneaks through.
    const handleLocalToggle = (emp_id, app_column) => {
        if (!isEditing) return;
        setEmployees(prev => prev.map(emp => {
            if (emp.emp_id !== emp_id) return emp;
            const app = APPS.find(a => a.key === app_column);
            if (app?.topTierOnly && !isTopTier(emp.emp_access_level)) return emp;   // hard guard
            return { ...emp, [app_column]: emp[app_column] === 1 ? 0 : 1 };
        }));
    };

    // "ALL" toggle - flips every app the row is ALLOWED to have. Top-tier
    // apps are skipped for Manager/User rows so a User's ALL toggle never
    // grants dAdmin/dEpr/dSpr access.
    const handleLocalToggleAll = (emp_id) => {
        if (!isEditing) return;
        const empRow = employees.find(x => x.emp_id === emp_id);
        if (!empRow) return;
        const allowedApps = APPS.filter(a => !a.topTierOnly || isTopTier(empRow.emp_access_level));
        const allOn = allowedApps.every(a => empRow[a.key] === 1);
        const next = allOn ? 0 : 1;
        setEmployees(prev => prev.map(emp => {
            if (emp.emp_id !== emp_id) return emp;
            const patch = {};
            allowedApps.forEach(a => { patch[a.key] = next; });
            return { ...emp, ...patch };
        }));
    };

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setEmployees(JSON.parse(JSON.stringify(originalEmployees)));
        setIsEditing(false);
    };

    const handleSave = async () => {
        setSaving(true);
        const updates = [];
        employees.forEach(emp => {
            const orig = originalEmployees.find(o => o.emp_id === emp.emp_id);
            if (!orig) return;
            APPS.forEach(a => {
                if (emp[a.key] !== orig[a.key]) {
                    updates.push(apiFetch("/api/AccessCenter/emp_app_is_active_toggle", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ emp_id: emp.emp_id, app_column: a.key, new_value: emp[a.key] }),
                        credentials: "include",
                    }));
                }
            });
        });

        try {
            if (updates.length === 0) {
                dnAlert.info("No changes", "Nothing to save.");
            } else {
                await Promise.all(updates);
                dnAlert.success("Saved", `${updates.length} change${updates.length === 1 ? "" : "s"} applied.`);
            }
            setIsEditing(false);
            await fetchEmployees();
        } catch (err) {
            dnAlert.error("Save failed", err.message || "Try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="eaa-container">
            <div className={`eaa-dashboard ${navSize}`}>

                {/* ── Header (matches Payslip Management / Generate Payslips) ── */}
                <div className="eaa-header-wrapper">
                    <h2 className="eaa-header-h2">
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo"
                                style={{ height: "50px", objectFit: "contain", marginLeft: "-20px", marginRight: "10px" }} />
                        </a>
                        App configuration
                    </h2>
                    <p className="eaa-subtitle">
                        Grant or revoke individual dApp access per employee. dAdmin / dEpr are restricted to Admin and Sub Admin tier; dSpr is open to all employees (needed for self-review form).
                    </p>
                </div>

                {/* ── Stat tiles ──────────────────────────────────────────── */}
                <div className="eaa-stats-grid">
                    <div className="eaa-stat eaa-stat-blue">
                        <div className="eaa-stat-label">EMPLOYEES (FILTERED)</div>
                        <div className="eaa-stat-num">{stats.total}</div>
                    </div>
                    <div className="eaa-stat eaa-stat-red">
                        <div className="eaa-stat-label">ADMINS</div>
                        <div className="eaa-stat-num">{stats.admins}</div>
                    </div>
                    <div className="eaa-stat eaa-stat-amber">
                        <div className="eaa-stat-label">SUB ADMINS</div>
                        <div className="eaa-stat-num">{stats.subAdmins}</div>
                    </div>
                    <div className="eaa-stat eaa-stat-green">
                        <div className="eaa-stat-label">FULL ACCESS</div>
                        <div className="eaa-stat-num">{stats.fullAccess}</div>
                    </div>
                </div>

                {/* ── Filter bar + action buttons ─────────────────────────── */}
                <div className="eaa-toolbar">
                    <div className="eaa-toolbar-left">
                        <div className="eaa-field">
                            <label>FILTER BY</label>
                            <Select
                                classNamePrefix="eaa-sel"
                                options={mainFilterOptions}
                                value={mainFilterOptions.find(f => f.value === selectedMainFilter)}
                                onChange={(val) => {
                                    setSelectedMainFilter(val.value);
                                    setFilters(prev => ({ ...prev, [val.value]: [] }));
                                }}
                                isSearchable={false}
                                /* Render the menu in a portal at body level so it
                                   escapes the sticky table header's stacking context
                                   (otherwise the dropdown gets clipped behind the
                                   navy header below). */
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                        <div className="eaa-field eaa-field-grow">
                            <label>VALUES</label>
                            <Select
                                classNamePrefix="eaa-sel"
                                placeholder={`Select ${mainFilterOptions.find(f => f.value === selectedMainFilter)?.label}...`}
                                options={getFilterValues(selectedMainFilter)}
                                value={filters[selectedMainFilter]}
                                onChange={vals => setFilters(prev => ({ ...prev, [selectedMainFilter]: vals || [] }))}
                                isMulti
                                {...checkboxMultiProps}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                        <div className="eaa-field eaa-field-grow">
                            <label>SEARCH</label>
                            <input type="text" className="eaa-search"
                                placeholder="Name, ID, Access level, Email..."
                                value={search}
                                onChange={e => setSearch(e.target.value)} />
                        </div>
                    </div>
                    <div className="eaa-toolbar-right">
                        {!isEditing && (
                            <button type="button" className="eaa-btn eaa-btn-primary" onClick={handleEdit}>
                                ✎ Edit access
                            </button>
                        )}
                        {isEditing && (
                            <>
                                <button type="button" className="eaa-btn eaa-btn-ghost" onClick={handleCancel} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="button" className="eaa-btn eaa-btn-save" onClick={handleSave} disabled={saving}>
                                    {saving ? "Saving..." : "✓ Save changes"}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* ── Table ───────────────────────────────────────────────── */}
                <div className="eaa-table-wrap">
                    {loading ? (
                        <div className="eaa-loading">Loading employees...</div>
                    ) : filteredEmployees.length === 0 ? (
                        <div className="eaa-empty">
                            <div className="eaa-empty-icon">⊘</div>
                            <div>No employees match the current filters.</div>
                        </div>
                    ) : (
                        <table className="eaa-table">
                            <thead>
                                <tr>
                                    <th className="eaa-col-emp">Employee</th>
                                    <th className="eaa-col-access">Access Level</th>
                                    <th className="eaa-col-all">All</th>
                                    {APPS.map(app => (
                                        <th key={app.key} className={`eaa-col-app ${app.topTierOnly ? "eaa-col-app-restricted" : ""}`}>
                                            <span className="eaa-app-head">
                                                <span className="eaa-app-name">{app.label}</span>
                                                {app.topTierOnly && (
                                                    <span className="eaa-app-admin-tag" title="Admin or Sub Admin tier only">ADMIN</span>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEmployees.map(emp => {
                                    const topTier = isTopTier(emp.emp_access_level);
                                    const allowedApps = APPS.filter(a => !a.topTierOnly || topTier);
                                    const allOn = allowedApps.length > 0 && allowedApps.every(a => emp[a.key] === 1);
                                    const lvlMeta = accessLevelMeta(emp.emp_access_level);
                                    return (
                                        <tr key={emp.emp_id}>
                                            <td className="eaa-col-emp">
                                                <button type="button" className="eaa-emp-cell" onClick={() => setSelectedEmployee(emp)}>
                                                    <span className="eaa-avatar"
                                                        style={{ background: avatarColor(`${emp.emp_first_name} ${emp.emp_last_name}`) }}>
                                                        {initials(emp.emp_first_name, emp.emp_last_name)}
                                                    </span>
                                                    <span className="eaa-emp-text">
                                                        <span className="eaa-emp-name">{emp.emp_first_name} {emp.emp_last_name}</span>
                                                        <span className="eaa-emp-id">{emp.emp_id}</span>
                                                    </span>
                                                </button>
                                            </td>
                                            <td className="eaa-col-access">
                                                <span className="eaa-access-pill"
                                                    style={{ background: lvlMeta.bg, color: lvlMeta.fg }}>
                                                    {emp.emp_access_level || "-"}
                                                </span>
                                            </td>
                                            <td className="eaa-col-all">
                                                <label className="eaa-switch eaa-switch-master">
                                                    <input type="checkbox"
                                                        checked={allOn}
                                                        disabled={!isEditing}
                                                        onChange={() => handleLocalToggleAll(emp.emp_id)} />
                                                    <span className="eaa-slider" />
                                                </label>
                                            </td>
                                            {APPS.map(a => {
                                                const blocked = a.topTierOnly && !topTier;
                                                return (
                                                    <td key={a.key} className="eaa-col-app">
                                                        <label className={`eaa-switch ${blocked ? "eaa-switch-blocked" : ""}`}
                                                            title={blocked ? `${a.label} is Admin / Sub Admin only` : ""}>
                                                            <input type="checkbox"
                                                                checked={!blocked && emp[a.key] === 1}
                                                                disabled={!isEditing || blocked}
                                                                onChange={() => handleLocalToggle(emp.emp_id, a.key)} />
                                                            <span className="eaa-slider" />
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ── Employee Detail Modal ───────────────────────────────── */}
                {selectedEmployee && (
                    // No backdrop onClick - the modal closes only via the X button
                    // or Esc, matching every other modal in the app.
                    <div className="eaa-modal-backdrop">
                        <div className="eaa-modal">
                            <div className="eaa-modal-head">
                                <div className="eaa-modal-emp">
                                    <span className="eaa-avatar eaa-avatar-lg"
                                        style={{ background: avatarColor(`${selectedEmployee.emp_first_name} ${selectedEmployee.emp_last_name}`) }}>
                                        {initials(selectedEmployee.emp_first_name, selectedEmployee.emp_last_name)}
                                    </span>
                                    <div>
                                        <div className="eaa-modal-name">{selectedEmployee.emp_first_name} {selectedEmployee.emp_last_name}</div>
                                        <div className="eaa-modal-sub">{selectedEmployee.emp_id} · {selectedEmployee.emp_access_level || "-"}</div>
                                    </div>
                                </div>
                                <button type="button" className="eaa-modal-close" onClick={() => setSelectedEmployee(null)} aria-label="Close">
                                    ×
                                </button>
                            </div>
                            <div className="eaa-modal-body">
                                <div className="eaa-modal-grid">
                                    <div><span>Email</span><strong>{selectedEmployee.emp_mail_id || "-"}</strong></div>
                                    <div><span>Mobile</span><strong>{selectedEmployee.emp_mobile_no || "-"}</strong></div>
                                    <div><span>Department</span><strong>{selectedEmployee.emp_department || "-"}</strong></div>
                                    <div><span>Job Position</span><strong>{selectedEmployee.job_position || "-"}</strong></div>
                                    <div><span>Career Level</span><strong>{selectedEmployee.career_level || "-"}</strong></div>
                                    <div><span>Joining Date</span><strong>{selectedEmployee.joining_date || "-"}</strong></div>
                                </div>
                                <div className="eaa-modal-apps">
                                    <div className="eaa-modal-apps-title">Current App Access</div>
                                    <div className="eaa-modal-apps-grid">
                                        {APPS.map(a => {
                                            const blocked = a.topTierOnly && !isTopTier(selectedEmployee.emp_access_level);
                                            const on = !blocked && selectedEmployee[a.key] === 1;
                                            return (
                                                <div key={a.key} className={`eaa-modal-app ${on ? "on" : ""} ${blocked ? "blocked" : ""}`}>
                                                    <span className="eaa-modal-app-name">{a.label}</span>
                                                    <span className="eaa-modal-app-state">
                                                        {blocked ? "Admin tier only" : on ? "Enabled" : "Disabled"}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default EmployeeAppAccess;
